import { TextButton } from '@/components/ui/primitives';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useUI } from '@/state/store';
import { useTwin } from '../store';
import { resetModelView } from '../scene/resetView';
import { siteBridge } from './bridge';
import { SITE_PROFILES, siteProfile } from './profiles';
import { useAntennaSite } from './store';
import { facilityAssetId, facilityById } from '@/features/catalogue/facilities';
import { useFacilityTour } from './facilityTour';
import { useSiteTerrain } from './terrainState';
import { isRfims } from '@/features/rfims/registry';
import { useRoofImagery } from './roofImagery';
import { ChevronDown, Crosshair, Map, Mountain } from 'lucide-react';
import { twinActionClass } from '../ui/hud/styles';

export function SiteControls() {
  const navigate = useNavigate();
  useEffect(() => {
    siteBridge.openAsset = (id) => navigate(`/twin/${id}`);
    return () => {
      siteBridge.openAsset = null;
    };
  }, [navigate]);
  const { rangeM, detail, ready, imagery, error } = useAntennaSite();
  const interior = useTwin((s) => s.subviewId !== null);
  const profile = useTwin((s) => siteProfile(s.asset));
  const terrainEntry = useSiteTerrain((s) => (profile ? s.entries[profile.assetId] : undefined));
  const roofStatus = useRoofImagery((s) => (profile ? s.entries[profile.assetId] : undefined));
  const facilityModel = useTwin((s) =>
    s.asset?.procedural?.kind === 'facility' ? s.asset.procedural : null,
  );
  const cutaway = useFacilityTour((s) => s.openAsset === profile?.assetId);
  if (interior || !profile) return null;
  const facility = facilityById(profile.facilityId);
  const frame = (range: number) => {
    useUI.getState().select({ subsystemId: null });
    const anchor = siteBridge.anchor;
    useTwin.getState().requestCamera({
      pose: {
        target: anchor.toArray(),
        position:
          range > 1000000
            ? [anchor.x + range * 0.05, range, anchor.z]
            : [anchor.x - range * 0.45, range * 0.72, anchor.z + range * 0.53],
        fov: range > 1000000 ? 50 : 42,
      },
      durationMs: range > 10000 ? 2400 : 1400,
    });
  };
  return (
    <div
      className="w-full border-b border-hairline pb-1 text-[11px] text-ink-2"
      data-testid="antenna-site-controls"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div role="group" aria-label="Site camera" className="flex flex-wrap gap-1.5">
          <TextButton className={twinActionClass} onClick={resetModelView}>
            <Crosshair className="h-3.5 w-3.5" aria-hidden />
            {profile.assetId === 'rf_antenna' ? 'Antenna' : 'Inspect'}
          </TextButton>
          <TextButton
            className={twinActionClass}
            onClick={() =>
              frame(profile.kind === 'aircraft' ? 8000 : Math.max(380, (profile.extentM ?? 0) * 3))
            }
          >
            <Mountain className="h-3.5 w-3.5" aria-hidden />
            {profile.kind === 'campus' || profile.kind === 'optical' || facility ? 'Campus' : 'Surroundings'}
          </TextButton>
          <TextButton
            className={twinActionClass}
            disabled={!ready}
            onClick={() => frame(profile.kind === 'aircraft' ? 200000 : 12000)}
          >
            <Map className="h-3.5 w-3.5" aria-hidden />
            Region
          </TextButton>
        </div>
        <span className="min-w-0 truncate text-ink-2" title={profile.location}>
          {profile.location}
        </span>
        <span className="whitespace-nowrap font-mono text-[10px] text-ink-3" data-testid="site-scale">
          {rangeM < 1000
            ? `${Math.round(rangeM)} m`
            : `${(rangeM / 1000).toFixed(rangeM < 10000 ? 1 : 0)} km`}{' '}
          range
        </span>
        {facilityModel?.mappingNote?.includes('review required') && (
          <span className="text-amber-200">Placement unverified</span>
        )}
        {!cutaway && (
          <details
            key={profile.assetId}
            className="group/site ml-auto max-w-full open:ml-0 open:w-full"
            data-testid="site-details"
          >
            <summary className="flex min-h-7 w-fit cursor-pointer list-none items-center gap-1 rounded-control px-2 text-ink-3 hover:bg-overlay hover:text-ink [&::-webkit-details-marker]:hidden">
              Site details{' '}
              <ChevronDown
                className="h-3.5 w-3.5 transition-transform group-open/site:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="mt-1 max-h-48 space-y-2 overflow-y-auto rounded-control border border-hairline bg-inset/50 p-2 text-[11px] leading-relaxed text-ink-3">
              <p>
                {detail > 0.95
                  ? profile.kind === 'aircraft'
                    ? '70,000 ft · 21.336 km altitude'
                    : profile.kind === 'ocean'
                      ? 'Ocean surface'
                      : isRfims(profile.assetId)
                        ? 'Terrain & mapped buildings'
                        : 'Wireframe surroundings'
                  : detail > 0.05
                    ? 'Site → imagery'
                    : rangeM > 1000000
                      ? 'Earth'
                      : 'Satellite imagery'}{' '}
                · Scroll to explore
              </p>
              {facility && !facilityModel && (
                <Link
                  className="text-accent underline"
                  to={`/twin/${encodeURIComponent(facilityAssetId(facility.id))}`}
                >
                  Open {facility.name} ↗
                </Link>
              )}
              {isRfims(profile.assetId) && (
                <p className="mt-1 px-1 text-[10px] text-ink-3">
                  {terrainEntry?.surface
                    ? `Terrain-following placement · site elevation ${terrainEntry.surface.anchorEllipsoidM.toFixed(1)} m above WGS84 ellipsoid · aerial shoreline preserved`
                    : terrainEntry?.status === 'unavailable'
                      ? terrainEntry.error
                      : 'Loading terrain-following placement…'}{' '}
                  · Elevations are model estimates; imagery is not live.
                </p>
              )}
              {isRfims(profile.assetId) && terrainEntry?.status === 'unavailable' && (
                <button
                  className="px-1 text-accent underline"
                  onClick={() =>
                    void import('./terrainSampling').then((m) => m.loadSiteTerrain(profile)).catch(() => {})
                  }
                >
                  Retry terrain placement
                </button>
              )}
              {isRfims(profile.assetId) && (
                <p className="mt-1 px-1 text-[10px] text-ink-3">
                  {roofStatus === 'ready'
                    ? 'Aerial roof detail'
                    : roofStatus === 'partial'
                      ? 'Some roof imagery unavailable · untextured patches retained'
                      : roofStatus === 'unavailable'
                        ? 'Roof imagery unavailable'
                        : 'Loading aerial roof detail…'}{' '}
                  · {facilityModel?.features.filter((f) => f.roofSurface).length ?? 0} lidar roof surfaces;
                  other heights estimated · Source dates in Site & evidence.
                </p>
              )}
              {facilityModel && facilityModel.terminals.length > 0 && !cutaway && (
                <details className="mt-1 px-1" data-testid="facility-antennas">
                  <summary className="w-fit cursor-pointer text-accent">
                    Antennas at this facility ({facilityModel.terminals.length})
                  </summary>
                  <p className="my-1 max-w-3xl text-[10px] text-ink-3">
                    Catalogue records. Shared coordinates are shown together; exact installed positions are
                    not inferred. Select an antenna to inspect its equipment.
                  </p>
                  <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                    {facilityModel.terminals.map((t) => (
                      <Link
                        className="rounded border border-hairline px-2 py-1 hover:bg-overlay"
                        key={t.assetId}
                        to={`/twin/${encodeURIComponent(t.assetId)}`}
                      >
                        {t.label} · {t.diameterM} m ↗
                      </Link>
                    ))}
                  </div>
                </details>
              )}
              <div>
                <p className="font-medium text-ink-2">Location & model reference</p>
                <p className="mt-1 max-w-3xl leading-relaxed">
                  {Math.abs(profile.lat).toFixed(6)}° {profile.lat >= 0 ? 'N' : 'S'},{' '}
                  {Math.abs(profile.lon).toFixed(6)}° {profile.lon >= 0 ? 'E' : 'W'} — {profile.description}{' '}
                  {profile.mapUrl &&
                    'Roads: OpenStreetMap. Facility footprints: OpenStreetMap and Overture Maps (ODbL 1.0). Heights use source attributes where available; roofs, immediate surface and neighbouring model shapes are representative. '}
                  Imagery is not live.{' '}
                  <a className="underline" href={profile.sourceUrl} target="_blank" rel="noreferrer">
                    {profile.sourceTitle}
                  </a>
                  {' · '}
                  <a
                    className="underline"
                    href={`https://www.openstreetmap.org/#map=18/${profile.lat}/${profile.lon}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Map source
                  </a>
                </p>
                {profile.kind === 'campus' && (
                  <div className="mt-1 flex flex-wrap gap-3" aria-label="Wallops campus models">
                    {['rf_antenna', 'ground_station', 'data_center']
                      .filter((id) => id !== profile.assetId)
                      .map((id) => (
                        <Link key={id} className="underline text-accent" to={`/twin/${id}`}>
                          {SITE_PROFILES[id].title} ↗
                        </Link>
                      ))}
                  </div>
                )}
              </div>
              {facilityModel?.mappingNote && (
                <div>
                  <p className="font-medium text-ink-2">Building mapping & sources</p>
                  <p>
                    {facilityModel.mappingNote} © OpenStreetMap contributors and Overture Maps Foundation,
                    ODbL 1.0. Roofs and facade details are representative.
                  </p>
                  <a
                    href="https://docs.overturemaps.org/guides/buildings/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Overture building sources
                  </a>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
      {(error || imagery === 'offline') && (
        <p role="status" className="mt-1 px-1 text-ink-3">
          {error ??
            'Some aerial imagery tiles could not load. Available imagery and saved site geometry remain visible.'}
        </p>
      )}
    </div>
  );
}
